import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL, CUNEIFORM_TEXTURE } from "@/lib/brand";
import { Feather, Vote, ScrollText, ShieldCheck } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

function MaskedLine({ children, delay = 0, className = "" }) {
  return (
    <span className="block overflow-hidden">
      <motion.span
        className={`block ${className}`}
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 0.9, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function GoogleMark() {
  return (
    <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.5 6.1 29.5 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.6-.4-3.9z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.5 6.1 29.5 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C36.9 39.2 44 34 44 24c0-1.3-.1-2.6-.4-3.9z" />
    </svg>
  );
}

const FEATURES = [
  {
    icon: Feather,
    title: "المنشورات",
    text: "بيانات الاتحاد وإعلاناته، نصوص خالصة بلا ضجيج — يكتبها المفوَّضون فقط.",
    span: "md:col-span-7",
  },
  {
    icon: Vote,
    title: "التصويتات",
    text: "قرارات الاتحاد بأصواتكم: نعم، لا، أو امتناع. صوت واحد لكل عضو.",
    span: "md:col-span-5",
  },
  {
    icon: ShieldCheck,
    title: "عضوية موثوقة",
    text: "يُفعَّل كل حساب من الإدارة، ليبقى مجتمعنا طلابياً حقيقياً.",
    span: "md:col-span-5",
  },
  {
    icon: ScrollText,
    title: "النظام الداخلي",
    text: "دستور الاتحاد واضح ومتاح للجميع، يُحدَّث من لوحة التحكم.",
    span: "md:col-span-7",
  },
];

const MARQUEE_ITEMS = ["الحكمة", "العلم", "الكلمة", "وحدة الطلبة", "نابو", "العراق"];

export default function Landing() {
  const { user, loading } = useAuth();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const textureY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const heroContentY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);

  if (!loading && user) return <Navigate to="/posts" replace />;

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/posts";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-white text-foreground" data-testid="landing-page">
      <section ref={heroRef} className="relative flex min-h-[92vh] flex-col items-center justify-center overflow-hidden px-6">
        <motion.div aria-hidden className="absolute inset-0 opacity-[0.08] mix-blend-multiply" style={{ y: textureY }}>
          <img src={CUNEIFORM_TEXTURE} alt="" className="h-[130%] w-full object-cover" />
        </motion.div>
        <div aria-hidden className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,#FFFFFF_78%)]" />

        <motion.div className="relative z-10 flex flex-col items-center text-center" style={{ y: heroContentY }}>
          <motion.img
            src={LOGO_URL}
            alt="شعار اتحاد نابو"
            data-testid="landing-logo"
            className="mb-10 h-28 w-28 rounded-3xl border border-gold/50 shadow-[0_0_80px_rgba(212,175,55,0.35)] sm:h-36 sm:w-36"
            initial={{ opacity: 0, scale: 0.85, rotate: -4 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 1, ease: EASE }}
          />

          <p className="mb-4 text-sm font-bold tracking-normal text-gold-dark" data-testid="landing-eyebrow">
            <MaskedLine delay={0.25}>اتحاد طلابي عراقي</MaskedLine>
          </p>

          <h1 className="font-amiri text-7xl font-bold leading-none sm:text-8xl lg:text-9xl" data-testid="landing-title">
            <MaskedLine delay={0.4} className="text-gold-gradient pb-4">نابو</MaskedLine>
          </h1>

          <p className="mt-6 max-w-xl text-base leading-8 text-muted-foreground sm:text-lg">
            <MaskedLine delay={0.8}>
              منصة الاتحاد الرسمية — منشورات، تصويتات، ونظام داخلي بإدارة شفافة، بأيدي طلبة العراق.
            </MaskedLine>
          </p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.05, ease: EASE }}
            className="mt-10 flex flex-col items-center gap-3"
          >
            <button
              onClick={login}
              data-testid="login-google-button"
              className="group flex items-center gap-3 rounded-full bg-gold px-8 py-4 text-lg font-bold text-navy shadow-[0_8px_40px_rgba(212,175,55,0.4)] transition-[transform,box-shadow,background-color] duration-300 hover:-translate-y-1 hover:bg-gold-light hover:shadow-[0_16px_60px_rgba(212,175,55,0.5)] focus:outline-none focus:ring-2 focus:ring-gold focus:ring-offset-2 focus:ring-offset-white"
            >
              <span className="rounded-full bg-white p-1"><GoogleMark /></span>
              الدخول بحساب Google
            </button>
            <span className="text-xs text-muted-foreground">العضوية تُفعَّل بعد موافقة الإدارة</span>
          </motion.div>
        </motion.div>
      </section>

      <div className="overflow-hidden border-y border-gold/30 py-5" dir="ltr" data-testid="landing-marquee">
        <div className="flex w-max animate-marquee gap-10 whitespace-nowrap">
          {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS, ...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, i) => (
            <span key={i} className="flex items-center gap-10 font-amiri text-2xl text-gold-dark/40">
              {item}
              <span className="inline-block h-2 w-2 rotate-45 bg-gold/50" />
            </span>
          ))}
        </div>
      </div>

      <section className="bg-[#FAF8F3]">
        <div className="mx-auto max-w-6xl px-6 py-24">
          <motion.h3
            className="mb-12 font-amiri text-4xl text-navy sm:text-5xl"
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: EASE }}
          >
            بيتُ الطالبِ <span className="text-gold-gradient">العراقي</span>
          </motion.h3>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-12">
            {FEATURES.map((f, i) => (
              <motion.div
                key={f.title}
                data-testid={`feature-card-${i}`}
                className={`group rounded-2xl border border-gold/30 bg-white p-8 shadow-sm transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1 hover:border-gold/60 hover:shadow-[0_16px_40px_rgba(10,30,60,0.1)] ${f.span}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.1, ease: EASE }}
              >
                <f.icon className="mb-5 h-8 w-8 text-gold-dark transition-transform duration-300 group-hover:scale-110" />
                <h4 className="mb-3 font-amiri text-2xl text-navy">{f.title}</h4>
                <p className="leading-8 text-muted-foreground">{f.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <footer className="border-t border-gold/25 py-10">
        <div className="mx-auto flex max-w-6xl flex-col items-center gap-4 px-6">
          <img src={LOGO_URL} alt="نابو" className="h-12 w-12 rounded-xl border border-gold/40" />
          <p className="text-sm text-muted-foreground">نابو — الاتحاد الطلابي العراقي © 2026</p>
        </div>
      </footer>
    </div>
  );
}
